import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'universal-cookie';
const cookies = new Cookies();

export interface AgencyReportRow {
  id: number | null;
  user_id: number;
  name: string;
  email: string;
  company?: string | null;
  total: number;
  message: number;
  message_responded: number;
  missed_call: number;
  answered_call: number;
}

export interface AggregatedStats {
  total: number;
  message: number;
  message_responded: number;
  missed_call: number;
  answered_call: number;
}

export interface AgencyReportsState {
  reports: AgencyReportRow[];
  aggregated: AggregatedStats | null;
  weeks: string[];
  current_week: string | null;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null;
  loading: boolean;
}

export const agencyReportsGet = createAsyncThunk<
  {
    data: AgencyReportRow[];
    aggregated: AggregatedStats;
    weeks: string[];
    current_week: string;
    pagination: AgencyReportsState['pagination'];
  },
  { agencyId: number; week?: string; page: number; perPage: number; search?: string }
>('agencyReports/get', async ({ agencyId, week, page = 1, perPage = 20, search }) => {
  const params = new URLSearchParams();
  if (week) params.append('week', week);
  params.append('page', String(page));
  params.append('perPage', String(perPage));
  if (search) params.append('search', search);

  const token = cookies.get('gls-token');
  const res = await fetch(`api/agency-weekly-report/${agencyId}?${params.toString()}`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch agency reports (${res.status})`);
  }

  const json = await res.json();

  return json as {
    data: AgencyReportRow[];
    aggregated: AggregatedStats;
    weeks: string[];
    current_week: string;
    pagination: AgencyReportsState['pagination'];
  };
});

const initialState: AgencyReportsState = {
  reports: [],
  aggregated: null,
  weeks: [],
  current_week: null,
  pagination: null,
  loading: false,
};

export const agencyReportsSlice = createSlice({
  name: 'agencyReports',
  initialState,
  reducers: {
    clearAgencyReports: (state) => {
      state.reports = [];
      state.pagination = null;
      state.aggregated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(agencyReportsGet.pending, (state) => {
        state.loading = true;
      })
      .addCase(agencyReportsGet.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.reports = payload.data;
        state.aggregated = payload.aggregated;
        state.weeks = payload.weeks;
        state.current_week = payload.current_week;
        state.pagination = payload.pagination;
      })
      .addCase(agencyReportsGet.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearAgencyReports } = agencyReportsSlice.actions;

export default agencyReportsSlice.reducer; 